import {
  db,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "../../../config/firebase";
import { ERROR_MESSAGES } from "../types/publicGalleryTypes";
import {
  generateGalleryId,
  validateGalleryData,
  getNextDisplayOrder,
} from "../utils/publicGalleryHelpers";

class PublicGalleryService {
  getGalleryCollection() {
    return collection(db, "publicGalleries");
  }

  convertTimestamp(value) {
    if (!value) return null;
    if (typeof value === "object" && value.seconds !== undefined) {
      return new Date(
        value.seconds * 1000 + (value.nanoseconds || 0) / 1000000,
      ).toISOString();
    }
    if (value instanceof Date) return value.toISOString();
    return value;
  }

  convertDocData(docData) {
    if (!docData) return docData;
    const converted = { ...docData };
    ["createdAt", "updatedAt"].forEach((field) => {
      if (converted[field]) {
        converted[field] = this.convertTimestamp(converted[field]);
      }
    });
    return converted;
  }

  // ============ GALLERY CRUD ============

  async getGalleries(filters = {}) {
    try {
      let q = query(this.getGalleryCollection(), orderBy("createdAt", "desc"));

      if (filters.status && filters.status !== "all") {
        q = query(q, where("status", "==", filters.status));
      }
      if (filters.category && filters.category !== "all") {
        q = query(q, where("category", "==", filters.category));
      }

      const snapshot = await getDocs(q);
      let galleries = [];
      snapshot.forEach((docSnap) => {
        galleries.push({
          ...this.convertDocData(docSnap.data()),
          id: docSnap.id,
        });
      });

      galleries = galleries.filter((g) => g.isDeleted !== true);

      if (filters.search) {
        const s = filters.search.toLowerCase();
        galleries = galleries.filter(
          (g) =>
            g.name?.toLowerCase().includes(s) ||
            g.description?.toLowerCase().includes(s) ||
            g.tags?.some((t) => t.toLowerCase().includes(s)),
        );
      }

      return galleries;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getGalleryById(id) {
    try {
      const docRef = doc(db, "publicGalleries", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);
      return {
        ...this.convertDocData(docSnap.data()),
        id: docSnap.id,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createGallery(data, createdBy) {
    try {
      const errors = validateGalleryData(data);
      if (Object.keys(errors).length > 0) {
        throw new Error(Object.values(errors)[0]);
      }

      const galleryData = {
        galleryCode: generateGalleryId(),
        eventId: data.eventId || "",
        name: data.name,
        description: data.description || "",
        category: data.category,
        status: data.status || "draft",
        isPublic: data.isPublic ?? false,
        displayOrder: data.displayOrder || 0,
        coverImageUrl: data.coverImageUrl || null,
        coverThumbnailUrl: data.coverThumbnailUrl || null,
        galleryThumbnailUrl: data.galleryThumbnailUrl || null,
        settings: {
          allowDownload: data.settings?.allowDownload ?? true,
          allowComments: data.settings?.allowComments ?? false,
          allowShare: data.settings?.allowShare ?? true,
          showWatermark: data.settings?.showWatermark ?? false,
        },
        images: [],
        statistics: {
          totalImages: 0,
          totalViews: 0,
          totalShares: 0,
        },
        tags: data.tags || [],
        createdBy: {
          id: createdBy?.id || "USR001",
          name: createdBy?.name || "Admin",
        },
        isDeleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(this.getGalleryCollection(), galleryData);
      const newDoc = await getDoc(docRef);
      return {
        ...this.convertDocData(newDoc.data()),
        id: newDoc.id,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateGallery(id, data) {
    try {
      const docRef = doc(db, "publicGalleries", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);

      const safeData = { ...data };
      delete safeData.id;
      delete safeData.galleryCode;

      await updateDoc(docRef, { ...safeData, updatedAt: serverTimestamp() });
      const updatedDoc = await getDoc(docRef);
      return {
        ...this.convertDocData(updatedDoc.data()),
        id: updatedDoc.id,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteGallery(id) {
    try {
      const docRef = doc(db, "publicGalleries", id);
      await updateDoc(docRef, {
        isDeleted: true,
        updatedAt: serverTimestamp(),
      });
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async permanentlyDeleteGallery(id) {
    try {
      await deleteDoc(doc(db, "publicGalleries", id));
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async archiveGallery(id) {
    return this.updateGallery(id, { status: "archived" });
  }

  async publishGallery(id) {
    return this.updateGallery(id, { status: "active", isPublic: true });
  }

  async unpublishGallery(id) {
    return this.updateGallery(id, { isPublic: false });
  }

  // ============ IMAGE MANAGEMENT ============
  // Note: image objects use plain Date strings, NOT serverTimestamp(),
  // because serverTimestamp() is not supported inside array elements.

  async uploadImages(galleryId, imagesData) {
    try {
      const gallery = await this.getGalleryById(galleryId);
      const startOrder = getNextDisplayOrder(gallery.images || []);

      const newImages = imagesData.map((img, index) => ({
        url: img.url,
        thumbnailUrl: img.thumbnailUrl || img.url,
        displayOrder: startOrder + index,
        isVisible: true,
        isDeleted: false,
        uploadedAt: new Date().toISOString(),
      }));

      const images = [...(gallery.images || []), ...newImages];
      const totalImages = images.filter((i) => !i.isDeleted).length;

      const updateData = {
        images,
        statistics: {
          ...gallery.statistics,
          totalImages,
        },
      };

      if (!gallery.coverImageUrl && newImages.length > 0) {
        updateData.coverImageUrl = newImages[0].url;
        updateData.coverThumbnailUrl = newImages[0].thumbnailUrl;
      }

      return await this.updateGallery(galleryId, updateData);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteImage(galleryId, imageUrl) {
    try {
      const gallery = await this.getGalleryById(galleryId);
      const images = (gallery.images || []).map((img) =>
        img.url === imageUrl ? { ...img, isDeleted: true } : img,
      );
      const totalImages = images.filter((i) => !i.isDeleted).length;

      const updateData = {
        images,
        statistics: { ...gallery.statistics, totalImages },
      };

      if (gallery.coverImageUrl === imageUrl) {
        const nextVisible = images.find((i) => !i.isDeleted);
        updateData.coverImageUrl = nextVisible?.url || null;
        updateData.coverThumbnailUrl = nextVisible?.thumbnailUrl || null;
      }

      return await this.updateGallery(galleryId, updateData);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async toggleImageVisibility(galleryId, imageUrl) {
    try {
      const gallery = await this.getGalleryById(galleryId);
      const images = (gallery.images || []).map((img) =>
        img.url === imageUrl ? { ...img, isVisible: !img.isVisible } : img,
      );
      return await this.updateGallery(galleryId, { images });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async reorderImages(galleryId, orderedUrls) {
    try {
      const gallery = await this.getGalleryById(galleryId);
      const images = (gallery.images || []).map((img) => {
        const newOrder = orderedUrls.indexOf(img.url);
        return newOrder !== -1 ? { ...img, displayOrder: newOrder + 1 } : img;
      });
      return await this.updateGallery(galleryId, { images });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async setCoverImage(galleryId, imageUrl, thumbnailUrl) {
    return this.updateGallery(galleryId, {
      coverImageUrl: imageUrl,
      coverThumbnailUrl: thumbnailUrl || imageUrl,
    });
  }

  // ============ SETTINGS ============

  async updateSettings(galleryId, settingsUpdates) {
    try {
      const gallery = await this.getGalleryById(galleryId);
      const settings = { ...gallery.settings, ...settingsUpdates };
      return await this.updateGallery(galleryId, { settings });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ STATISTICS ============

  async incrementViews(galleryId) {
    try {
      const gallery = await this.getGalleryById(galleryId);
      const statistics = {
        ...gallery.statistics,
        totalViews: (gallery.statistics?.totalViews || 0) + 1,
      };
      return await this.updateGallery(galleryId, { statistics });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async incrementShares(galleryId) {
    try {
      const gallery = await this.getGalleryById(galleryId);
      const statistics = {
        ...gallery.statistics,
        totalShares: (gallery.statistics?.totalShares || 0) + 1,
      };
      return await this.updateGallery(galleryId, { statistics });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getOverallStats() {
    try {
      const galleries = await this.getGalleries();
      const active = galleries.filter((g) => g.status === "active");
      const totalImages = galleries.reduce(
        (sum, g) => sum + (g.statistics?.totalImages || 0),
        0,
      );
      const totalViews = galleries.reduce(
        (sum, g) => sum + (g.statistics?.totalViews || 0),
        0,
      );
      const totalShares = galleries.reduce(
        (sum, g) => sum + (g.statistics?.totalShares || 0),
        0,
      );

      return {
        totalGalleries: galleries.length,
        activeGalleries: active.length,
        totalImages,
        totalViews,
        totalShares,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ PUBLIC (CLIENT-FACING) ============

  async getPublicGalleryById(id) {
    try {
      const gallery = await this.getGalleryById(id);
      if (!gallery.isPublic || gallery.status !== "active") {
        throw new Error("Gallery is not publicly accessible");
      }
      gallery.images = (gallery.images || []).filter(
        (img) => !img.isDeleted && img.isVisible,
      );
      return gallery;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getPublicGalleries() {
    try {
      const q = query(
        this.getGalleryCollection(),
        where("isPublic", "==", true),
        where("status", "==", "active"),
        orderBy("displayOrder", "asc"),
      );
      const snapshot = await getDocs(q);
      const galleries = [];
      snapshot.forEach((docSnap) => {
        const data = this.convertDocData(docSnap.data());
        if (data.isDeleted !== true) {
          galleries.push({ ...data, id: docSnap.id });
        }
      });
      return galleries;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  handleError(error) {
    console.error("Firebase error:", error);
    if (error.code === "permission-denied") {
      return new Error("You don't have permission to perform this action.");
    }
    return new Error(error.message || "An error occurred");
  }
}

const publicGalleryService = new PublicGalleryService();
export default publicGalleryService;
